import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ComboField } from '@components/Fields/ComboField';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useData } from '@hooks/useData';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { MenuItem, Typography, makeStyles } from '@material-ui/core';
import { ICliente } from '@model/Cliente';
import { EnumEmpresa, IComboEmpresa } from '@model/Empresa';
import { IFaturaItem } from '@model/FaturaItem';
import { IRegistrarFaturaAvulsaRequest, useClienteService } from '@services/ClienteService';
import { yup } from '@validations/Yup';
import { addDays } from 'date-fns';
import React from 'react';
import { FaturaRegistrarFormulario } from './FaturaRegistrarFormulario';

export type FaturaRegistrarModalProps = {
    modal: IModalWindow
    cliente: ICliente
    empresaCombo: IComboEmpresa
    onFaturaRegistrada: VoidFunction
}

export function FaturaRegistrarModal ({ modal, cliente, empresaCombo, onFaturaRegistrada }: FaturaRegistrarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const cls = useStyles();

    const clienteService = useClienteService();

    const form = useForm<Partial<IRegistrarFaturaAvulsaRequest>>({
        empresaId: useField(EnumEmpresa.None),
        dataVencimento: useField(addDays(new Date(), 10)),
        quantidade: useField(1),
        descricaoRemessa: useField(),
        faturaTipo: useField()
    })

    const validation = useValidation<IRegistrarFaturaAvulsaRequest>({
        dataVencimento: yup.date().required(),
        quantidade: yup.number().required().moreThan(0),
        descricaoRemessa: yup.string().trim().required(),
    })

    const data = useData<IFaturaItem>();

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            if (data.isEmpty) {
                message.warning("A fatura não possui itens");
            }
            else {
                const values = form.getInputValues();

                form.clearErrors();
                validation.validate(values)
                    .then(() => {
                        loading.start();

                        values.faturaItens = data.items;

                        clienteService.registrarFaturaManual(cliente.clienteId, values)
                            .then(response => {
                                message.success("Faturas registradas com sucesso");
                                modal.close();
                                onFaturaRegistrada();
                            })
                            .catch(form.setErrorsFromServer)
                            .finally(loading.stop);
                    })
                    .catch(form.setErrorsFromYup);
            }
        }
    }, [cliente.clienteId, clienteService, data.isEmpty, data.items, form, loading, message, modal, onFaturaRegistrada, validation]);

    const handleEnter = React.useCallback(() => {
        form.reset();
        data.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ModalWindow 
            modal={modal}
            size="md"
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle title="Registro de Fatura Manual" />
            
            <ModalWindowContent>
                <FaturaRegistrarFormulario 
                    cliente={cliente}
                    form={form}
                    data={data}
                    loading={loading}
                    empresaCombo={empresaCombo}
                />
            </ModalWindowContent>
            
            <ModalWindowActions>
                <Typography variant="body2">Tipo da Fatura: </Typography>
                <ComboField 
                    field={form.fields.faturaTipo}
                    className={cls.tipoFatura}
                    updateFieldStateOnChange
                >
                    <MenuItem key="Antecipada" value="Antecipada">
                        <HorizontalGrid>
                            <Typography><b>Antecipada</b></Typography>
                            <Typography>&nbsp;(MOBI </Typography>
                            <Typography className={cls.naoGerara}>NÃO IRÁ GERAR</Typography>
                            <Typography> faturas automaticamente)</Typography>
                        </HorizontalGrid>
                    </MenuItem>

                    <MenuItem key="Avulsa" value="Avulsa">
                        <HorizontalGrid>
                            <Typography><b>Avulsa</b></Typography>
                            <Typography>&nbsp;(MOBI </Typography>
                            <Typography className={cls.gerara}>IRÁ GERAR</Typography>
                            <Typography> faturas automaticamente)</Typography>
                        </HorizontalGrid>
                    </MenuItem>
                </ComboField>

                <PrimaryButton onClick={handleConfirm} disabled={form.fields.faturaTipo.value === undefined}>Registrar Fatura</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}

const useStyles = makeStyles(theme => ({
    tipoFatura: {
        width: 500, 
    },

    naoGerara: {
        color: 'red',
        fontWeight: 'bold',
        marginLeft: 5,
        marginRight: 5
    },

    gerara: {
        color: 'darkgreen',
        fontWeight: 'bold',
        marginLeft: 5,
        marginRight: 5
    }
}));